import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';

import type { BinningMode, HistogramConfig, YAxisType } from './histogram-chart.model';

@Component({
  selector: 'dojo-histogram-config-panel',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonToggleModule, MatChipsModule, NumberInputComponent, TextInputComponent],
  templateUrl: './histogram-config-panel.component.html',
  styleUrl: './histogram-config-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistogramConfigPanelComponent {
  readonly config = input.required<HistogramConfig>();
  readonly configChange = output<HistogramConfig>();

  readonly binningMode = computed(() => this.config().binning.mode);
  readonly yAxisType = computed(() => this.config().yAxisType);

  readonly binningDescription = computed(() => {
    switch (this.binningMode()) {
      case 'count':
        return 'Divide the data range into a fixed number of equal-width bins';
      case 'width':
        return 'Each bin has a fixed width (e.g. intervals of 5)';
      case 'custom':
        return 'Define your own class boundaries, allowing unequal widths';
    }
  });

  readonly yAxisDescription = computed(() => {
    switch (this.yAxisType()) {
      case 'frequency':
        return 'Count of data points in each bin';
      case 'relative-frequency':
        return 'Proportion of data in each bin (count / total)';
      case 'frequency-density':
        return 'Frequency / class width — area of each bar equals its relative frequency';
    }
  });

  readonly binCountCtrl = new FormControl<number>(10, { nonNullable: true });
  readonly binWidthCtrl = new FormControl<number>(5, { nonNullable: true });
  readonly boundaries = signal<number[]>([]);
  readonly startBoundaryTextCtrl = new FormControl<string>('', { nonNullable: true });
  readonly separatorKeyCodes = [ENTER, COMMA] as const;

  // Convert FormControl valueChanges to signals (avoids subscribe)
  private readonly binWidthValue = toSignal(this.binWidthCtrl.valueChanges);
  private readonly startBoundaryTextValue = toSignal(this.startBoundaryTextCtrl.valueChanges);

  // Sync config input → FormControls
  private readonly syncEffect = effect(() => {
    const cfg = this.config();
    untracked(() => {
      if (cfg.binning.mode === 'count') {
        this.binCountCtrl.setValue(cfg.binning.binCount, { emitEvent: false });
      }
      if (cfg.binning.mode === 'width') {
        this.binWidthCtrl.setValue(cfg.binning.binWidth, { emitEvent: false });
      }
      if (cfg.binning.mode === 'custom') {
        this.boundaries.set([...cfg.binning.boundaries]);
      }
      this.startBoundaryTextCtrl.setValue(cfg.startBoundary !== null ? String(cfg.startBoundary) : '', {
        emitEvent: false,
      });
    });
  });

  // FormControl changes → emit config (guarded by current mode)
  private readonly binWidthEffect = effect(() => {
    const width = this.binWidthValue();
    if (width !== undefined && width > 0 && this.binningMode() === 'width') {
      untracked(() => this.configChange.emit({ ...this.config(), binning: { mode: 'width', binWidth: width } }));
    }
  });

  private readonly startBoundaryEffect = effect(() => {
    const str = this.startBoundaryTextValue();
    if (str !== undefined) {
      const parsed = str.trim() === '' ? null : Number(str);
      const boundary = parsed !== null && isNaN(parsed) ? null : parsed;
      untracked(() => this.configChange.emit({ ...this.config(), startBoundary: boundary }));
    }
  });

  onBinningModeChange(mode: BinningMode): void {
    const cfg = this.config();
    switch (mode) {
      case 'count':
        this.configChange.emit({ ...cfg, binning: { mode: 'count', binCount: this.binCountCtrl.value } });
        break;
      case 'width':
        this.configChange.emit({ ...cfg, binning: { mode: 'width', binWidth: this.binWidthCtrl.value } });
        break;
      case 'custom': {
        const b = this.boundaries();
        this.configChange.emit({
          ...cfg,
          binning: { mode: 'custom', boundaries: b.length >= 2 ? b : [0, 50, 100] },
        });
        break;
      }
    }
  }

  onBinCountSlider(count: number): void {
    this.binCountCtrl.setValue(count, { emitEvent: false });
    this.configChange.emit({ ...this.config(), binning: { mode: 'count', binCount: count } });
  }

  onYAxisTypeChange(type: YAxisType): void {
    this.configChange.emit({ ...this.config(), yAxisType: type });
  }

  addBoundary(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    const num = Number(value);
    if (value && !isNaN(num)) {
      const updated = [...this.boundaries(), num].sort((a, b) => a - b);
      this.boundaries.set(updated);
      this.emitBoundaries(updated);
    }
    event.chipInput.clear();
  }

  removeBoundary(value: number): void {
    const updated = this.boundaries().filter((b) => b !== value);
    this.boundaries.set(updated);
    this.emitBoundaries(updated);
  }

  private emitBoundaries(boundaries: number[]): void {
    if (boundaries.length >= 2) {
      this.configChange.emit({ ...this.config(), binning: { mode: 'custom', boundaries } });
    }
  }
}
